import { HttpErrorResponse } from '@angular/common/http';
import { AfterViewInit, Component, ElementRef, Input, OnInit, ViewChild } from '@angular/core';
import { MatSnackBar as MatSnackBar } from '@angular/material/snack-bar';

import { Organization, Project, Report } from 'src/app/shared/models';
import { AppErrorStateMatcher, ReportService } from 'src/app/shared/services';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { ConfirmDialogComponent, ConfirmDialogModel } from 'src/app/shared/components';
import { MatDialog as MatDialog } from '@angular/material/dialog';

import { EditorState } from '@codemirror/state';
import { EditorView, keymap, lineNumbers, highlightActiveLine, highlightActiveLineGutter } from '@codemirror/view';
import { yaml } from '@codemirror/lang-yaml';
import { oneDark } from '@codemirror/theme-one-dark';
import { defaultKeymap, history, historyKeymap } from '@codemirror/commands';
import { syntaxHighlighting, defaultHighlightStyle, bracketMatching, foldGutter } from '@codemirror/language';
import { highlightSelectionMatches } from '@codemirror/search';

@Component({
    selector: 'app-reports',
    templateUrl: './reports.component.html',
    styleUrls: ['./reports.component.scss'],
    standalone: false
})
export class ReportsComponent implements OnInit, AfterViewInit {
  public loading = 0;
  public initialized = false;

  @Input() project: Project;
  @Input() organization: Organization;
  @Input() canEdit = true;
  @ViewChild('editorContainer') editorContainer: ElementRef;
  public reports: Report[] = [];
  public report: Report;
  public newReport: Report; // for acl

  public matcher = new AppErrorStateMatcher();
  public inputForm: UntypedFormGroup = this.fb.group({
    id: [''],
    name: ['', [Validators.required]],
    template: ['', [Validators.required]],
    report_type: [null],
  });

  public reportTypes = [
    { value: 'standard', label: 'Standard report' },
    { value: 'change_order_request', label: 'Change order request' },
    { value: 'change_order', label: 'Change order' },
    { value: 'offsite_test', label: 'Offsite test' },
    { value: 'rfi_detail', label: 'RFI detail' },
  ];

  private editorView: EditorView;

  constructor(
    public dialog: MatDialog,
    private fb: UntypedFormBuilder,
    private reportSrv: ReportService,
    private snackBar: MatSnackBar
  ) { }

  ngOnInit(): void {
    this.newReport = new Report({
      project: this.project,
      organization: this.organization,
    });
    this.fetchRecords();
  }

  ngAfterViewInit(): void {
    this.initEditor();
  }

  private initEditor(): void {
    if (!this.editorContainer?.nativeElement) return;

    const updateListener = EditorView.updateListener.of((update) => {
      if (update.docChanged) {
        const value = update.state.doc.toString();
        this.inputForm.controls.template.setValue(value, { emitEvent: false });
        this.inputForm.controls.template.markAsDirty();
      }
    });

    const state = EditorState.create({
      doc: this.inputForm.controls.template.value || '',
      extensions: [
        lineNumbers(),
        highlightActiveLineGutter(),
        history(),
        foldGutter(),
        bracketMatching(),
        highlightActiveLine(),
        highlightSelectionMatches(),
        syntaxHighlighting(defaultHighlightStyle, { fallback: true }),
        yaml(),
        keymap.of([...defaultKeymap, ...historyKeymap]),
        updateListener,
        EditorView.theme({
          '&': {
            fontSize: '13px',
            fontFamily: "'JetBrains Mono', 'Fira Code', 'Source Code Pro', 'Consolas', monospace",
          },
          '.cm-content': {
            minHeight: '500px',
          },
          '.cm-gutters': {
            backgroundColor: '#f8f9fb',
            borderRight: '1px solid #E9ECF4',
            color: '#8B94B3',
          },
          '.cm-activeLineGutter': {
            backgroundColor: '#e8ecf4',
          },
          '&.cm-focused': {
            outline: '2px solid #4F46E5',
            outlineOffset: '-1px',
          },
        }),
        EditorState.readOnly.of(!this.canEdit),
        EditorView.lineWrapping,
      ],
    });

    this.editorView = new EditorView({
      state,
      parent: this.editorContainer.nativeElement,
    });
  }

  private updateEditorContent(content: string): void {
    if (!this.editorView) return;
    const currentContent = this.editorView.state.doc.toString();
    if (currentContent !== content) {
      this.editorView.dispatch({
        changes: {
          from: 0,
          to: currentContent.length,
          insert: content || '',
        },
      });
    }
  }

  fetchRecords(): void {
    this.loading++;
    const qp = { include: ['project', 'organization'] };
    this.reportSrv.getReports(this.project, this.organization, qp)
      .then((reports: Report[]) => {
        // Hide system-default templates (NULL project + NULL org) — they exist
        // only as render-time fallbacks and shouldn't clutter the management list.
        this.reports = reports.filter(r => r.project_id || r.organization_id);

        if (this.reports.length) {  // select first template
          this.changeSelection(this.reports[0]);
        }
      })
      .catch((resp: HttpErrorResponse) => {
        this.snackBar.open(resp.error.error, '', { duration: 5000 });
      })
      .finally(() => {
        this.loading--;
        this.initialized = true;
      });
  }

  /**
   * Toggles input form
   * @param model Report
   * @param show boolean
   */
  toggleForm(model: Report = null, show = true): void {
    if (show) {
      this.inputForm.reset({
        id: model?.id || null,
        name: model?.name || null,
        template: model?.template || null,
        report_type: model?.report_type || null,
      });
      this.report = model;
      this.updateEditorContent(model?.template || '');
    }
  }

  save(form: UntypedFormGroup) {
    if (!form.valid) {
      return;
    }
    const payload = form.value;
    if (this.project) {
      payload.project_id = this.project?.id;
    }

    this.loading++;
    const qp = { include: ['project', 'organization'] };
    const req = payload?.id
      ? this.reportSrv.update(payload, qp)
      : this.reportSrv.create(this.project, this.organization, payload, qp);

    req.then((report: Report) => {
      this.snackBar.open(`Saved "${report?.name}"`, '', { duration: 5000 });

      report.selected = true;
      const i = this.reports.findIndex(o => o.id === report.id);
      if (i >= 0) {
        this.reports.splice(i, 1, report);
      } else {
        this.reports.push(report);
      }
      this.changeSelection(report);
      this.toggleForm(report, false);
    })
      .catch((resp: HttpErrorResponse) => {
        if (resp.status === 422) {
          this.matcher.setServerErrors(form, resp);
          return;
        }
        this.snackBar.open(
          resp.error?.error || 'Oops! something went wrong.',
          '',
          { duration: 5000 },
        );
      })
      .finally(() => {
        this.loading--;
      });
  }

  changeSelection(report: Report) {
    if (!report) {
      this.report = null;
      return;
    };
    this.report = report;

    // for initial selection
    this.reports.map((o) => o.selected = false);
    this.report.selected = true;

    this.toggleForm(this.report, true);
  }

  selectionChange(e: any) {
    this.changeSelection(e?.source?._value[0] || null);
  }

  getTypeLabel(type: string): string {
    return this.reportTypes.find(t => t.value === type)?.label || type;
  }

  toggleReportType(type: string): void {
    const currentType = this.inputForm.controls.report_type.value;
    const newType = currentType === type ? null : type;

    // Check if another template already has this type
    if (newType) {
      const currentId = this.report?.id;
      const conflict = this.reports.find(r => r.report_type === type && r.id !== currentId);

      if (conflict) {
        const typeLabel = this.getTypeLabel(type);
        const dialogRef = this.dialog.open(ConfirmDialogComponent, {
          disableClose: true,
          data: new ConfirmDialogModel(
            'Reassign template?',
            `<b>${conflict.name}</b> is already the ${typeLabel} template. Move it to this one instead?`
          ),
        });

        dialogRef.afterClosed().subscribe(result => {
          if (result) {
            conflict.report_type = null;
            if (conflict.id) {
              this.saveReportType(conflict, null);
            }
            this.applyReportType(newType);
          }
        });
        return;
      }
    }

    this.applyReportType(newType);
  }

  private applyReportType(type: string): void {
    this.inputForm.controls.report_type.setValue(type);
    if (this.report) {
      this.report.report_type = type;
    }
    // Auto-save only for existing templates
    if (this.report?.id) {
      this.saveReportType(this.report, type);
    }
  }

  private saveReportType(report: Report, type: string): void {
    const payload = { id: report.id, name: report.name, template: report.template, report_type: type };
    this.reportSrv.update(payload, {})
      .then(() => {
        this.snackBar.open(`Updated type for "${report.name}"`, '', { duration: 3000 });
        // Re-fetch list so conflict check has fresh report_type data
        this.refreshReportTypes();
      })
      .catch(() => {
        this.snackBar.open('Failed to update report type', '', { duration: 5000 });
      });
  }

  private refreshReportTypes(): void {
    const qp = { include: ['project', 'organization'] };
    this.reportSrv.getReports(this.project, this.organization, qp)
      .then((reports: Report[]) => {
        // Update report_type on local reports without replacing the array (to keep selection)
        reports.forEach(fresh => {
          const local = this.reports.find(r => r.id === fresh.id);
          if (local) {
            local.report_type = fresh.report_type;
          }
        });
      })
      .catch(() => {});
  }

  /**
   * Deletes Template
   * @param model
   */
  delete(model: Report): void {
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      disableClose: true,
      data: new ConfirmDialogModel(
        'Delete?',
        `Deleting <b>${model.name}</b> cannot be undone. <br />Proceed to delete?`
      ),
    });

    dialogRef.afterClosed().subscribe(result => {
      if (!result) {
        return;
      }

      this.reportSrv.delete(model)
        .then((resp: any) => {
          this.snackBar.open(resp.message, '', { duration: 3000 });
          this.reports.splice(
            this.reports.findIndex(o => o.id === model.id),
            1,
          );
          this.changeSelection(this.reports[0] || null);
        })
        .catch((resp: HttpErrorResponse) => {
          this.snackBar.open(
            resp.error?.error || 'Oops! something went wrong.',
            '',
            { duration: 5000 },
          );
        });
    });
  }
}
